import { clsx } from 'clsx';
import type { LineFulfillmentStatus, PendingLinesPanelProps } from './MasterDocument.types';
import { LineFulfillmentBadge } from './LineFulfillmentBadge';
import { Loading } from './components/loading';
import { useMasterDocumentTranslation } from './i18n';

/**
 * PendingLinesPanel — panel de selección de líneas pendientes de los documentos
 * ORIGEN para el cruce (FE-02 de FEAT-094).
 *
 * Por cada línea muestra item, documento, cantidades (ordenada/cumplida/pendiente)
 * y su badge de estado (renderiza el `lineStatus` autoritativo del backend, no lo
 * recalcula). Las líneas seleccionadas habilitan el campo "Cantidad a cruzar"
 * editable con validación inline. Las líneas terminales (`Fulfilled`/`Closed`)
 * aparecen en gris y deshabilitadas.
 */

function isTerminalStatus(status: LineFulfillmentStatus): boolean {
  return status === 'Fulfilled' || status === 'Closed';
}

const HEADER_CLASS =
  'px-3 py-2 text-xs font-bold uppercase tracking-wide text-content-secondary dark:text-dark-content-secondary';
const CELL_CLASS = 'px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary align-top';

export function PendingLinesPanel({
  lines,
  selectedLineIds,
  quantities,
  errors,
  loading,
  onLineSelect,
  onTrySelectTerminalLine,
  onQuantityChange,
  formatNumber,
  className,
}: PendingLinesPanelProps) {
  const { t } = useMasterDocumentTranslation();
  const fmt = (value: number | undefined): string => {
    if (value == null) return '—';
    return formatNumber ? formatNumber(value) : String(value);
  };

  if (loading) {
    return (
      <Loading
        size="md"
        label={t('master_document.createFromSource.loadingLines')}
        className={className}
      />
    );
  }

  if (lines.length === 0) {
    return (
      <div
        className={clsx(
          'py-8 px-4 text-center text-sm text-content-secondary dark:text-dark-content-secondary',
          'border border-dashed border-border-primary dark:border-dark-border-primary rounded-lg',
          className,
        )}
      >
        {t('master_document.crossing.noPendingLinesAll')}
      </div>
    );
  }

  return (
    <div className={clsx('overflow-x-auto', className)}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border-primary dark:border-dark-border-primary">
            <th className={clsx(HEADER_CLASS, 'w-11')} aria-label={t('master_document.crossing.selectRow')} />
            <th className={clsx(HEADER_CLASS, 'text-left')}>{t('master_document.crossing.pending.item')}</th>
            <th className={clsx(HEADER_CLASS, 'text-left')}>{t('master_document.crossing.pending.document')}</th>
            <th className={clsx(HEADER_CLASS, 'text-right')}>{t('master_document.crossing.pending.pending')}</th>
            <th className={clsx(HEADER_CLASS, 'text-left')}>{t('master_document.crossing.pending.state')}</th>
            <th className={clsx(HEADER_CLASS, 'text-left')}>{t('master_document.crossing.pending.quantityToCross')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const terminal = isTerminalStatus(line.lineStatus);
            const isSelected = selectedLineIds.includes(line.lineId);
            const error = errors?.[line.lineId];
            const qty = quantities[line.lineId];

            return (
              <tr
                key={line.lineId}
                className={clsx(
                  'border-b border-border-primary/60 dark:border-dark-border-primary/60',
                  terminal && 'opacity-50',
                )}
              >
                <td className={clsx(CELL_CLASS, 'text-center')}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={terminal}
                    onChange={(e) => {
                      if (terminal) {
                        onTrySelectTerminalLine?.(line.lineId);
                        return;
                      }
                      onLineSelect(line.lineId, e.target.checked);
                    }}
                    className={clsx(
                      'w-4 h-4 rounded border-border-primary accent-primary-custom-600',
                      'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 focus:ring-offset-2',
                      'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
                      terminal && 'cursor-not-allowed',
                    )}
                    aria-label={t('master_document.crossing.pending.selectLineAria', {
                      item: line.itemCode ?? line.lineId,
                    })}
                  />
                </td>

                <td className={CELL_CLASS}>
                  <div className="font-medium">{line.itemCode ?? '—'}</div>
                  {line.itemDescription && (
                    <div className="text-xs text-content-secondary dark:text-dark-content-secondary">
                      {line.itemDescription}
                    </div>
                  )}
                </td>

                <td className={clsx(CELL_CLASS, 'whitespace-nowrap')}>
                  {line.documentNumber ?? '—'}
                </td>

                <td className={clsx(CELL_CLASS, 'text-right whitespace-nowrap tabular-nums')}>
                  {fmt(line.quantityPending)}
                  {line.unitOfMeasure ? ` ${line.unitOfMeasure}` : ''}
                </td>

                <td className={CELL_CLASS}>
                  <LineFulfillmentBadge status={line.lineStatus} />
                </td>

                <td className={CELL_CLASS}>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={line.quantityPending}
                    value={isSelected && qty != null ? qty : ''}
                    disabled={terminal || !isSelected}
                    onChange={(e) => {
                      const raw = e.target.value;
                      onQuantityChange(line.lineId, raw === '' ? NaN : Number(raw));
                    }}
                    placeholder={isSelected ? fmt(line.quantityPending) : ''}
                    aria-label={t('master_document.crossing.pending.quantityAria', {
                      item: line.itemCode ?? line.lineId,
                    })}
                    aria-invalid={error ? true : undefined}
                    className={clsx(
                      'w-28 px-2 py-1 rounded-md text-sm tabular-nums',
                      'border bg-bg-primary dark:bg-dark-bg-primary',
                      'text-content-primary dark:text-dark-content-primary',
                      'focus:outline-hidden focus:ring-2 focus:ring-primary-custom-400 dark:focus:ring-dark-border-custom',
                      error
                        ? 'border-red-500 dark:border-red-400'
                        : 'border-border-primary dark:border-dark-border-primary',
                      (terminal || !isSelected) && 'opacity-50 cursor-not-allowed',
                    )}
                  />
                  {error && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
